import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  deleteLLMGatewayProvider,
  discoverLLMGatewayModels,
  getLLMGatewayAttachmentParsers,
  type LLMGatewayAttachmentParserModels,
  type LLMGatewayCapabilitySource,
  type LLMGatewayModelCapabilities,
  type LLMGatewayProvider,
  type LLMGatewayProviderInput,
  type LLMGatewayRemoteModel,
  testLLMGatewayProvider,
  updateLLMGatewayAttachmentParsers,
  upsertLLMGatewayProvider
} from "./api";
import type { Translator } from "./i18n";

interface LLMGatewayProvidersProps {
  csrfToken: string;
  loading: boolean;
  providers: LLMGatewayProvider[];
  refresh: () => Promise<void>;
  t: Translator;
}

const EMPTY_CAPABILITIES: LLMGatewayModelCapabilities = {
  context_length: null,
  max_completion_tokens: null,
  supported_parameters: null,
  reasoning: {
    supported: null,
    mandatory: null,
    default_enabled: null,
    supported_efforts: null,
    default_effort: null
  },
  architecture: { input_modalities: [], output_modalities: [] }
};

// 输入模态是"能不能把原图/文件直接交给这个模型"的唯一依据：Core 的
// _setting_model_accepts_image_input 与 Pi sidecar 的 model.image 都只看
// capabilities.architecture.input_modalities 里有没有 image。未声明即按不支持处理，
// 因此管理员必须能在这里声明或采纳 OpenRouter 的建议值。
const INPUT_MODALITY_CHOICES: Array<{ key: string; label: [string, string]; hint: [string, string] }> = [
  { key: "image", label: ["图片", "Image"], hint: ["允许把原图直接发给该模型（无需解析模型）", "Send original images straight to this model (no parser needed)"] },
  { key: "file", label: ["文件", "File"], hint: ["允许把文件内容直接发给该模型", "Send file content straight to this model"] },
  { key: "audio", label: ["音频", "Audio"], hint: ["允许把音频直接发给该模型", "Send audio straight to this model"] },
  { key: "video", label: ["视频", "Video"], hint: ["允许把视频直接发给该模型", "Send video straight to this model"] }
];
const INPUT_MODALITIES_SOURCE_KEY = "architecture.input_modalities";

// Prices are decimal USD per million tokens (how provider price lists are
// written). A blank field means "not changing the price", so the key is left
// out of the payload rather than sent as "" or 0; the stored price is
// preserved. There is no clear signal on purpose: the deployment is updated
// with PATCH, which keeps an omitted price in force, so dropping only our copy
// would silently disagree with what actually prices the request. Enter 0 to
// stop charging a model.
function providerCostFields(inputCost: string, outputCost: string, cacheReadCost: string): Partial<LLMGatewayProviderInput> {
  const fields: Partial<LLMGatewayProviderInput> = {};
  const input = inputCost.trim();
  const output = outputCost.trim();
  const cacheRead = cacheReadCost.trim();
  if (input) fields.input_cost_per_million_usd = input;
  if (output) fields.output_cost_per_million_usd = output;
  if (cacheRead) fields.cache_read_cost_per_million_usd = cacheRead;
  return fields;
}

function cloneCapabilities(value: LLMGatewayModelCapabilities): LLMGatewayModelCapabilities {
  return {
    ...value,
    supported_parameters: value.supported_parameters ? [...value.supported_parameters] : null,
    reasoning: {
      ...value.reasoning,
      supported_efforts: value.reasoning.supported_efforts ? [...value.reasoning.supported_efforts] : null
    },
    architecture: {
      input_modalities: [...value.architecture.input_modalities],
      output_modalities: [...value.architecture.output_modalities]
    }
  };
}

function emptyRemoteModel(id = ""): LLMGatewayRemoteModel {
  return {
    id,
    custom: true,
    capabilities: cloneCapabilities(EMPTY_CAPABILITIES),
    options: { reasoning_effort: null },
    capability_sources: {},
    capability_references: { openrouter: null }
  };
}

function providerModel(provider: LLMGatewayProvider): LLMGatewayRemoteModel {
  const modelID = provider.upstream_model.replace(/^(openai|anthropic)\//, "");
  const stored = provider.remote_model_catalog?.find((item) => item.id === modelID)
    ?? provider.remote_model_catalog?.[0];
  if (stored) return stored;
  return {
    ...emptyRemoteModel(modelID),
    custom: false,
    capabilities: {
      ...cloneCapabilities(EMPTY_CAPABILITIES),
      context_length: provider.context_window,
      max_completion_tokens: provider.max_output_tokens,
      reasoning: { ...EMPTY_CAPABILITIES.reasoning, supported: provider.reasoning_supported }
    },
    options: { reasoning_effort: provider.reasoning_effort || null }
  };
}

function buildAPIBase(apiHost: string, apiPath: string): string {
  const input = apiHost.trim();
  if (!input) return "";
  try {
    const url = new URL(input.includes("://") ? input : `https://${input}`);
    const path = url.pathname.replace(/\/$/, "") || "/";
    const endpointPath = `/${apiPath.trim().replace(/^\/+|\/+$/g, "")}`;
    if (endpointPath !== "/" && path.endsWith(endpointPath)) {
      url.pathname = path.slice(0, -endpointPath.length) || "/";
    } else if (path === "/") url.pathname = "/v1";
    else if (path.endsWith("/compatible-mode")) url.pathname = `${path}/v1`;
    return url.toString().replace(/\/$/, "");
  } catch {
    return "";
  }
}

function displayAPIHost(apiBase: string): string {
  try {
    const url = new URL(apiBase);
    const path = url.pathname.replace(/\/$/, "") || "/";
    if (path === "/v1") return url.origin;
    if (path.endsWith("/compatible-mode/v1")) {
      url.pathname = path.slice(0, -3);
      return url.toString().replace(/\/$/, "");
    }
    return apiBase.replace(/\/$/, "");
  } catch {
    return apiBase;
  }
}

function providerHostname(value: string): string {
  try {
    return new URL(value.includes("://") ? value : `https://${value}`).hostname;
  } catch {
    return value.replace(/^https?:\/\//, "");
  }
}

function compactTokenCount(value: number | null): string {
  if (!value) return "";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(value);
}

function providerSummary(provider: LLMGatewayProvider): string {
  const selected = providerModel(provider);
  return [
    providerHostname(provider.api_base),
    selected.options.reasoning_effort?.trim(),
    compactTokenCount(selected.capabilities.context_length)
  ].filter(Boolean).join(" · ");
}

function providerInput(provider: LLMGatewayProvider, patch: Partial<LLMGatewayProviderInput> = {}): LLMGatewayProviderInput {
  const selected = providerModel(provider);
  return {
    model_ref: provider.model_name,
    upstream_model: selected.id,
    api_base: provider.api_base,
    api_path: provider.api_path || (provider.api_compat_mode === "anthropic" ? "/messages" : "/chat/completions"),
    api_compat_mode: provider.api_compat_mode || "openai",
    api_key: "",
    display_name: provider.display_name,
    context_window: selected.capabilities.context_length,
    max_output_tokens: selected.capabilities.max_completion_tokens,
    ...providerCostFields(provider.input_cost_per_million_usd ?? "", provider.output_cost_per_million_usd ?? "", provider.cache_read_cost_per_million_usd ?? ""),
    reasoning_supported: selected.capabilities.reasoning.supported,
    reasoning_effort: selected.options.reasoning_effort || "",
    enabled: provider.enabled !== false,
    is_default: provider.is_default === true,
    remote_model_catalog: catalogWithSelected(provider.remote_model_catalog, selected),
    ...patch
  };
}

function catalogWithSelected(
  catalog: LLMGatewayRemoteModel[] | null | undefined,
  selected: LLMGatewayRemoteModel
): LLMGatewayRemoteModel[] {
  const items = [...(catalog ?? [])];
  const index = items.findIndex((item) => item.id === selected.id);
  if (index >= 0) items[index] = selected;
  else items.push(selected);
  return items;
}

function useHoverActionDelay(delayMilliseconds = 500) {
  const [ready, setReady] = useState(false);
  const timer = useRef<number | null>(null);
  const clear = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const onMouseEnter = useCallback(() => {
    clear();
    setReady(false);
    timer.current = window.setTimeout(() => setReady(true), delayMilliseconds);
  }, [clear, delayMilliseconds]);
  const onMouseLeave = useCallback(() => {
    clear();
    setReady(false);
  }, [clear]);
  useEffect(() => clear, [clear]);
  return { ready, onMouseEnter, onMouseLeave };
}

function ToggleSwitch({ checked, label, onChange }: { checked: boolean; label: string; onChange: (checked: boolean) => void }) {
  return <button
    type="button"
    className={`legacy-model-toggle ${checked ? "is-on" : ""}`}
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={(event) => { event.stopPropagation(); onChange(!checked); }}
  ><span /></button>;
}

function CompatibilitySelect({ value, disabled, t, onChange }: {
  value: "openai" | "anthropic";
  disabled: boolean;
  t: Translator;
  onChange: (value: "openai" | "anthropic") => void;
}) {
  const [open, setOpen] = useState(false);
  const options: Array<{ value: "openai" | "anthropic"; label: string }> = [
    { value: "openai", label: `OpenAI API ${t("兼容", "compatible")}` },
    { value: "anthropic", label: `Anthropic API ${t("兼容", "compatible")}` }
  ];
  const selected = options.find((option) => option.value === value) ?? options[0];
  return <div className="legacy-config-select">
    <button
      type="button"
      className={`legacy-config-select-trigger ${open ? "is-open" : ""}`}
      aria-haspopup="listbox"
      aria-expanded={open}
      disabled={disabled}
      onClick={() => setOpen((current) => !current)}
    >
      <span>{selected.label}</span>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className={open ? "is-open" : ""}>
        <path d="M2.75 4.5 6 7.75 9.25 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
    {open && !disabled ? <>
      <button type="button" aria-label="close" className="legacy-config-select-dismiss" onClick={() => setOpen(false)} />
      <div className="legacy-config-select-options" role="listbox">
        {options.map((option) => <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={option.value === value}
          className={option.value === value ? "is-selected" : ""}
          onClick={() => { onChange(option.value); setOpen(false); }}
        >
          <span className="legacy-config-select-check">{option.value === value ? <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1.5 5 4 7.5 8.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg> : null}</span>
          <span>{option.label}</span>
        </button>)}
      </div>
    </> : null}
  </div>;
}

function RowAction({ kind, label, ready, onClick }: { kind: "edit" | "delete"; label: string; ready: boolean; onClick: () => void }) {
  return <button
    type="button"
    className={`legacy-model-row-action ${kind === "delete" ? "danger" : ""} ${ready ? "is-ready" : ""}`}
    aria-label={label}
    title={label}
    onClick={(event) => { event.stopPropagation(); onClick(); }}
  >
    {kind === "edit" ? <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10.8 3.2 12.8 5.2 5.6 12.4H3.6V10.4L10.8 3.2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><path d="M9.4 4.6l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
      : <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 4.5h9M6.2 4.5V3.4c0-.5.4-.9.9-.9h1.8c.5 0 .9.4.9.9v1.1M6.4 7.2v4.1M9.6 7.2v4.1M4.8 4.5l.5 8.2c0 .6.5 1.1 1.1 1.1h3.2c.6 0 1.1-.5 1.1-1.1l.5-8.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
    <span>{label}</span>
  </button>;
}

function ProviderRow({
  provider,
  t,
  onDefault,
  onEdit,
  onDelete,
  onToggle
}: {
  provider: LLMGatewayProvider;
  t: Translator;
  onDefault: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const hover = useHoverActionDelay();
  const name = provider.display_name || provider.model_name;
  return <li
    className={`legacy-model-row ${provider.is_default ? "is-default" : ""}`}
    onMouseEnter={hover.onMouseEnter}
    onMouseLeave={hover.onMouseLeave}
    onClick={() => { if (!provider.is_default) onDefault(); }}
  >
    <div className="legacy-model-row-name">
      <span>{name}</span>
      {providerSummary(provider) ? <small>{providerSummary(provider)}</small> : null}
    </div>
    {!provider.is_default ? <span className="legacy-model-default-hint">{t("点击设为默认", "Click to set as default")}</span> : null}
    <div className="legacy-model-row-actions" onClick={(event) => event.stopPropagation()}>
      {provider.is_default ? <span className="legacy-model-default-badge">{t("默认", "Default")}</span> : null}
      <RowAction kind="edit" label={t("编辑", "Edit")} ready={hover.ready} onClick={onEdit} />
      <RowAction kind="delete" label={t("删除", "Delete")} ready={hover.ready} onClick={onDelete} />
      <ToggleSwitch checked={provider.enabled !== false} label={t(`启用或关闭模型供应商 ${name}`, `Enable or disable model provider ${name}`)} onChange={onToggle} />
    </div>
  </li>;
}

interface ProviderFormSnapshot {
  compatibility: "openai" | "anthropic";
  apiHost: string;
  apiPath: string;
  apiKey: string;
  displayName: string;
  inputCostPerMillionUSD: string;
  outputCostPerMillionUSD: string;
  cacheReadCostPerMillionUSD: string;
  selectedModel: LLMGatewayRemoteModel;
}

function emptyProviderForm(): ProviderFormSnapshot {
  return {
    compatibility: "openai",
    apiHost: "",
    apiPath: "/chat/completions",
    apiKey: "",
    displayName: "",
    inputCostPerMillionUSD: "",
    outputCostPerMillionUSD: "",
    cacheReadCostPerMillionUSD: "",
    selectedModel: emptyRemoteModel()
  };
}

function formFromProvider(provider: LLMGatewayProvider): ProviderFormSnapshot {
  return {
    compatibility: provider.api_compat_mode || "openai",
    apiHost: displayAPIHost(provider.api_base),
    apiPath: provider.api_path || (provider.api_compat_mode === "anthropic" ? "/messages" : "/chat/completions"),
    apiKey: "",
    displayName: provider.display_name === provider.model_name ? "" : provider.display_name,
    inputCostPerMillionUSD: provider.input_cost_per_million_usd ?? "",
    outputCostPerMillionUSD: provider.output_cost_per_million_usd ?? "",
    cacheReadCostPerMillionUSD: provider.cache_read_cost_per_million_usd ?? "",
    selectedModel: providerModel(provider)
  };
}

function ModelPicker({ models, initialID, t, onClose, onConfirm }: {
  models: LLMGatewayRemoteModel[];
  initialID: string;
  t: Translator;
  onClose: () => void;
  onConfirm: (model: LLMGatewayRemoteModel) => void;
}) {
  const [query, setQuery] = useState("");
  const [selectedID, setSelectedID] = useState(models.some((item) => item.id === initialID) ? initialID : "");
  const filtered = models.filter((item) => item.id.toLowerCase().includes(query.trim().toLowerCase()));
  return <div className="legacy-modal-backdrop level-two" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="legacy-model-picker" role="dialog" aria-modal="true" aria-labelledby="model-picker-title">
      <header><h2 id="model-picker-title">{t("选择模型", "Select model")}</h2><p>{t(`选择一个模型（共 ${models.length} 个）`, `Select a model (${models.length} total)`)}</p></header>
      <div className="legacy-model-picker-body">
        <div className="legacy-model-picker-search"><label htmlFor="model-picker-search">{t("搜索模型", "Search models")}</label><input id="model-picker-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("搜索模型", "Search models")} autoComplete="off" autoFocus /></div>
        <div className="legacy-model-picker-list">
          {models.length === 0 ? <div className="legacy-model-picker-empty">{t("没有获取到模型", "No models were returned")}</div>
            : filtered.length === 0 ? <div className="legacy-model-picker-empty">{t("没有匹配的模型", "No matching models")}</div>
              : <ul>{filtered.map((model) => <li key={model.id}><label className={selectedID === model.id ? "selected" : ""}><input type="radio" name="remote-model" checked={selectedID === model.id} onChange={() => setSelectedID(model.id)} /><span>{model.id}</span></label></li>)}</ul>}
        </div>
        {!selectedID && models.length > 0 ? <p className="legacy-model-picker-validation">{t("请选择一个模型", "Select a model")}</p> : null}
      </div>
      <footer><button type="button" className="legacy-secondary-button" onClick={onClose}>{t("取消", "Cancel")}</button><button type="button" className="legacy-primary-button" disabled={!selectedID} onClick={() => { const selected = models.find((item) => item.id === selectedID); if (selected) onConfirm(selected); }}>{t("确认", "Confirm")}</button></footer>
    </section>
  </div>;
}

function RecommendationButton({ available, applied, t, onApply }: { available: boolean; applied: boolean; t: Translator; onApply: () => void }) {
  return <button type="button" className={`legacy-recommend-button ${applied ? "applied" : available ? "available" : ""}`} disabled={!available || applied} onClick={onApply}>
    {applied ? t("已采用推荐值", "Recommendation applied") : available ? t("采用推荐值", "Use recommendation") : t("暂无推荐值", "No recommendation")}
  </button>;
}

export function ModelOptions({ model, disabled, summaryAvailable, t, onChange }: { model: LLMGatewayRemoteModel; disabled: boolean; summaryAvailable: boolean; t: Translator; onChange: (model: LLMGatewayRemoteModel) => void }) {
  const reference = model.capability_references.openrouter?.capabilities;
  const [effortMenuOpen, setEffortMenuOpen] = useState(false);
  const patchCapability = (key: "context_length" | "max_completion_tokens", raw: string) => {
    if (!/^\d*$/.test(raw)) return;
    const value = raw ? Number(raw) : null;
    const sources = { ...model.capability_sources };
    if (value) sources[key] = "manual"; else delete sources[key];
    onChange({ ...model, capabilities: { ...model.capabilities, [key]: value }, capability_sources: sources });
  };
  const applyCapability = (key: "context_length" | "max_completion_tokens", value: number) => onChange({
    ...model,
    capabilities: { ...model.capabilities, [key]: value },
    capability_sources: { ...model.capability_sources, [key]: "openrouter_reference" }
  });
  const patchReasoning = (supported: boolean, source: LLMGatewayCapabilitySource = "manual") => onChange({
    ...model,
    capabilities: { ...model.capabilities, reasoning: { ...model.capabilities.reasoning, supported } },
    options: supported ? model.options : { reasoning_effort: null, reasoning_summary: null },
    capability_sources: { ...model.capability_sources, "reasoning.supported": source }
  });
  const patchInputModalities = (next: string[], source: LLMGatewayCapabilitySource = "manual") => {
    // 对话模型天然接受文本；这里只声明额外模态，全不选等于"未声明"。
    const extras = Array.from(new Set(next.filter((item) => item && item !== "text"))).sort();
    const modalities = extras.length ? ["text", ...extras] : [];
    const sources = { ...model.capability_sources };
    if (extras.length) sources[INPUT_MODALITIES_SOURCE_KEY] = source;
    else delete sources[INPUT_MODALITIES_SOURCE_KEY];
    onChange({
      ...model,
      capabilities: {
        ...model.capabilities,
        architecture: { ...model.capabilities.architecture, input_modalities: modalities }
      },
      capability_sources: sources
    });
  };
  const toggleInputModality = (key: string) => {
    const current = model.capabilities.architecture.input_modalities;
    patchInputModalities(current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  };
  const referenceModalities = reference?.architecture?.input_modalities ?? [];
  const declaredModalities = model.capabilities.architecture.input_modalities;
  const referenceModalitiesApplied = referenceModalities.length > 0
    && referenceModalities.every((item) => declaredModalities.includes(item))
    && declaredModalities.every((item) => referenceModalities.includes(item));
  const patchEffort = (effort: string) => onChange({
    ...model,
    capabilities: effort.trim() ? { ...model.capabilities, reasoning: { ...model.capabilities.reasoning, supported: true } } : model.capabilities,
    options: { ...model.options, reasoning_effort: effort.trim() || null },
    capability_sources: effort.trim() ? { ...model.capability_sources, "reasoning.supported": "manual" } : model.capability_sources
  });
  const referenceEfforts = reference?.reasoning.supported_efforts ?? [];
  return <section className="legacy-model-advanced">
    <div className="legacy-model-advanced-heading"><h3>{t("高级", "Advanced")}</h3><p>{t("以下参数可以填写自定义值，也可以采用系统推荐值", "Enter custom values or use system recommendations")}</p></div>
    <div className="legacy-model-horizontal-field">
      <label htmlFor="model-reasoning-summary">{t("原生简短摘要", "Native concise summary")}</label>
      <div className="legacy-capability-control native-summary-control">
        <input id="model-reasoning-summary" type="checkbox"
          checked={model.options.reasoning_summary === "concise"}
          disabled={disabled || !summaryAvailable || model.capabilities.reasoning.supported !== true}
          onChange={(event) => onChange({ ...model, options: { ...model.options, reasoning_summary: event.target.checked ? "concise" : null } })} />
        <span>{t("仅在该模型线路已验证支持 Responses 简短摘要时开启。推理强度独立设置。", "Enable only after this model route has been verified to support concise Responses summaries. Reasoning effort is configured separately.")}</span>
      </div>
    </div>
    <div className="legacy-model-horizontal-field"><label>{t("输入模态", "Input modalities")}</label><div className="legacy-capability-control"><div className="legacy-modality-toggles" role="group" aria-label={t("输入模态", "Input modalities")}>{INPUT_MODALITY_CHOICES.map((choice) => { const on = declaredModalities.includes(choice.key); return <button key={choice.key} type="button" role="checkbox" aria-checked={on} title={t(choice.hint[0], choice.hint[1])} disabled={disabled} className={on ? "is-on" : ""} onClick={() => toggleInputModality(choice.key)}>{t(choice.label[0], choice.label[1])}</button>; })}</div><RecommendationButton available={referenceModalities.length > 0} applied={referenceModalitiesApplied} t={t} onApply={() => patchInputModalities(referenceModalities, "openrouter_reference")} /></div></div>
    <div className="legacy-model-horizontal-field"><label>{t("上下文大小", "Context size")}</label><div className="legacy-capability-control"><input inputMode="numeric" pattern="[0-9]*" value={model.capabilities.context_length ?? ""} onChange={(event) => patchCapability("context_length", event.target.value)} placeholder={t("留空则不传递", "Omit when empty")} disabled={disabled} /><RecommendationButton available={reference?.context_length != null} applied={reference?.context_length != null && reference.context_length === model.capabilities.context_length} t={t} onApply={() => { if (reference?.context_length) applyCapability("context_length", reference.context_length); }} /></div></div>
    <div className="legacy-model-horizontal-field"><label>{t("最大输出", "Maximum output")}</label><div className="legacy-capability-control"><input inputMode="numeric" pattern="[0-9]*" value={model.capabilities.max_completion_tokens ?? ""} onChange={(event) => patchCapability("max_completion_tokens", event.target.value)} placeholder={t("留空则不传递", "Omit when empty")} disabled={disabled} /><RecommendationButton available={reference?.max_completion_tokens != null} applied={reference?.max_completion_tokens != null && reference.max_completion_tokens === model.capabilities.max_completion_tokens} t={t} onApply={() => { if (reference?.max_completion_tokens) applyCapability("max_completion_tokens", reference.max_completion_tokens); }} /></div></div>
    <div className="legacy-model-horizontal-field"><label>{t("推理能力", "Reasoning capability")}</label><div className="legacy-capability-control"><div className="legacy-reasoning-radios" role="radiogroup" aria-label={t("推理能力", "Reasoning capability")}><button type="button" role="radio" aria-checked={model.capabilities.reasoning.supported === true} onClick={() => patchReasoning(true)}><i className={model.capabilities.reasoning.supported === true ? "selected" : ""}/>{t("支持", "Supported")}</button><button type="button" role="radio" aria-checked={model.capabilities.reasoning.supported === false} onClick={() => patchReasoning(false)}><i className={model.capabilities.reasoning.supported === false ? "selected" : ""}/>{t("不支持", "Unsupported")}</button></div><RecommendationButton available={reference?.reasoning.supported != null} applied={reference?.reasoning.supported != null && reference.reasoning.supported === model.capabilities.reasoning.supported} t={t} onApply={() => { if (reference?.reasoning.supported != null) patchReasoning(reference.reasoning.supported, "openrouter_reference"); }} /></div></div>
    <div className="legacy-model-horizontal-field"><label>{t("推理强度", "Reasoning effort")}</label><div className="legacy-capability-control"><input value={model.options.reasoning_effort ?? ""} onChange={(event) => patchEffort(event.target.value)} placeholder={t("输入推理强度", "Enter reasoning effort")} disabled={disabled || model.capabilities.reasoning.supported === false} /><div className="legacy-effort-menu"><button type="button" className="legacy-recommend-select" aria-haspopup="listbox" aria-expanded={effortMenuOpen} disabled={disabled || model.capabilities.reasoning.supported === false || referenceEfforts.length === 0} onClick={() => setEffortMenuOpen((open) => !open)}>{referenceEfforts.length ? t("选择推荐值", "Select recommendation") : t("暂无推荐值", "No recommendation")}{referenceEfforts.length ? <span className="legacy-recommend-chevron"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className={effortMenuOpen ? "is-open" : ""}><path d="M2.75 4.5 6 7.75 9.25 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></span> : null}</button>{effortMenuOpen ? <div className="legacy-effort-options" role="listbox">{referenceEfforts.map((effort) => <button key={effort} type="button" role="option" aria-selected={model.options.reasoning_effort === effort} onClick={() => { patchEffort(effort); setEffortMenuOpen(false); }}><span>{effort}</span>{model.options.reasoning_effort === effort ? <span>✓</span> : null}</button>)}</div> : null}</div></div></div>
  </section>;
}

function ProviderDialog({ provider, csrfToken, t, onClose, onSaved }: {
  provider: LLMGatewayProvider | null;
  csrfToken: string;
  t: Translator;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const edit = provider !== null;
  const [form, setForm] = useState<ProviderFormSnapshot>(() => provider ? formFromProvider(provider) : emptyProviderForm());
  const [initial] = useState(() => JSON.stringify(provider ? formFromProvider(provider) : emptyProviderForm()));
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [operationReason, setOperationReason] = useState("");
  const [pickerModels, setPickerModels] = useState<LLMGatewayRemoteModel[] | null>(null);
  const [remoteModelCatalog, setRemoteModelCatalog] = useState<LLMGatewayRemoteModel[]>(() => {
    if (provider?.remote_model_catalog?.length) return provider.remote_model_catalog;
    return provider ? [providerModel(provider)] : [];
  });
  const [feedback, setFeedback] = useState<{ tone: "success" | "error" | "info"; text: string } | null>(null);
  const base = buildAPIBase(form.apiHost, form.apiPath);
  const canSubmit = Boolean(
    base
    && form.apiPath.trim()
    && form.selectedModel.id.trim()
    && (edit || form.apiKey.trim())
    && (!edit || form.apiKey.trim() || JSON.stringify(form) !== initial)
    && operationReason.trim()
    && !saving
  );

  const discoveryInput = () => ({
    api_base: base,
    api_path: form.apiPath.trim(),
    api_compat_mode: form.compatibility,
    api_key: form.apiKey.trim(),
    model_ref: provider?.model_name ?? ""
  });

  const fetchModels = useCallback(async (showPicker: boolean) => {
    if (!base) {
      setFeedback({ tone: "error", text: t("API 主机格式不正确", "The API host is invalid") });
      return;
    }
    if (!edit && !form.apiKey.trim()) {
      setFeedback({ tone: "error", text: t("获取模型前请填写 API Key", "Enter the API key before fetching models") });
      return;
    }
    setFetching(true);
    setFeedback(null);
    try {
      const result = await discoverLLMGatewayModels(csrfToken, discoveryInput());
      setRemoteModelCatalog(result.items);
      if (showPicker) setPickerModels(result.items);
      const selected = result.items.find((item) => item.id === form.selectedModel.id);
      if (!showPicker && selected) setForm((current) => ({ ...current, selectedModel: mergeFetchedModel(current.selectedModel, selected) }));
      if (showPicker) {
        setFeedback({ tone: "success", text: result.endpoint
          ? t(`已从 ${result.endpoint} 获取 ${result.items.length} 个模型`, `${result.items.length} models fetched from ${result.endpoint}`)
          : t(`已获取 ${result.items.length} 个模型`, `${result.items.length} models fetched`) });
      }
    } catch (error) {
      if (showPicker) setFeedback({ tone: "error", text: error instanceof Error ? error.message : t("获取模型失败", "Failed to fetch models") });
    } finally {
      setFetching(false);
    }
  }, [base, csrfToken, edit, form.apiKey, form.apiPath, form.compatibility, form.selectedModel.id, provider?.model_name, t]);

  useEffect(() => {
    if (provider?.remote_model_catalog?.length) void fetchModels(false);
  // Refresh reference metadata only when the editor opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save() {
    if (!canSubmit) return;
    setSaving(true);
    let phase: "test" | "save" = "test";
    setFeedback({ tone: "info", text: t("正在测试连接…", "Testing connection…") });
    try {
      await testLLMGatewayProvider(csrfToken, { ...discoveryInput(), remote_model: form.selectedModel.id.trim() });
      setFeedback({ tone: "success", text: t("连接测试成功", "Connection test succeeded") });
      await new Promise((resolve) => window.setTimeout(resolve, 400));
      phase = "save";
      setFeedback({ tone: "info", text: t("正在保存…", "Saving…") });
      await upsertLLMGatewayProvider(csrfToken, operationReason.trim(), {
        model_ref: provider?.model_name ?? "",
        upstream_model: form.selectedModel.id.trim(),
        api_base: base,
        api_path: form.apiPath.trim().startsWith("/") ? form.apiPath.trim() : `/${form.apiPath.trim()}`,
        api_compat_mode: form.compatibility,
        api_key: form.apiKey.trim(),
        display_name: form.displayName.trim(),
        context_window: form.selectedModel.capabilities.context_length,
        max_output_tokens: form.selectedModel.capabilities.max_completion_tokens,
        reasoning_supported: form.selectedModel.capabilities.reasoning.supported,
        reasoning_effort: form.selectedModel.options.reasoning_effort || "",
        enabled: provider?.enabled ?? true,
        is_default: provider?.is_default ?? false,
        remote_model_catalog: catalogWithSelected(remoteModelCatalog, form.selectedModel),
        ...providerCostFields(form.inputCostPerMillionUSD, form.outputCostPerMillionUSD, form.cacheReadCostPerMillionUSD)
      });
      await onSaved();
      setFeedback({ tone: "success", text: t("保存成功", "Saved") });
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
      onClose();
    } catch (error) {
      const summary = phase === "test" ? t("连接测试失败", "Connection test failed") : t("保存失败", "Save failed");
      setFeedback({ tone: "error", text: error instanceof Error ? `${summary}: ${error.message}` : summary });
    } finally {
      setSaving(false);
    }
  }

  return <div className="legacy-modal-backdrop" onMouseDown={(event) => { if (!saving && event.target === event.currentTarget) onClose(); }}>
    <section className="legacy-provider-dialog" role="dialog" aria-modal="true" aria-labelledby="provider-dialog-title">
      <header><h2 id="provider-dialog-title">{t(edit ? "编辑模型供应商" : "添加模型供应商", edit ? "Edit model provider" : "Add model provider")}</h2></header>
      <div className="legacy-provider-dialog-body">
        <div className="legacy-model-fields">
          <div className="legacy-model-horizontal-field"><label>{t("API 兼容模式", "API compatibility")}<em>*</em></label><CompatibilitySelect value={form.compatibility} disabled={saving} t={t} onChange={(compatibility) => { setForm({ ...form, compatibility, apiPath: compatibility === "anthropic" ? "/messages" : "/chat/completions", selectedModel: { ...form.selectedModel, options: { ...form.selectedModel.options, reasoning_summary: null } } }); setFeedback(null); }} /></div>
          <div className="legacy-model-horizontal-field"><label>{t("API 主机", "API host")}<em>*</em></label><input type="url" value={form.apiHost} onChange={(event) => { setForm({ ...form, apiHost: event.target.value }); setFeedback(null); }} placeholder={t("例如 https://dashscope.aliyuncs.com/compatible-mode/v1", "For example https://api.example.com/v1")} disabled={saving} /></div>
          <div className="legacy-model-horizontal-field"><label>{t("API 路径", "API path")}<em>*</em></label><input value={form.apiPath} onChange={(event) => { setForm({ ...form, apiPath: event.target.value }); setFeedback(null); }} placeholder={form.compatibility === "anthropic" ? "/messages" : "/chat/completions"} disabled={saving} /></div>
          <div className="legacy-model-horizontal-field"><label>API Key</label><input type="password" value={form.apiKey} onChange={(event) => { setForm({ ...form, apiKey: event.target.value }); setFeedback(null); }} placeholder={edit ? t("留空则不修改 API Key", "Leave blank to keep the API key") : t("输入 API Key", "Enter API key")} autoComplete="new-password" disabled={saving} /></div>
          <div className="legacy-model-horizontal-field"><label>{t("模型", "Model")}<em>*</em></label><div className="legacy-model-fetch-control"><input value={form.selectedModel.id} onChange={(event) => setForm({ ...form, selectedModel: { ...form.selectedModel, id: event.target.value, custom: true, options: { ...form.selectedModel.options, reasoning_summary: null }, capability_references: { openrouter: null } } })} placeholder={t("输入模型 ID", "Enter model ID")} disabled={saving} /><button type="button" onClick={() => void fetchModels(true)} disabled={saving || fetching}>{fetching ? t("获取中…", "Fetching…") : t("获取", "Fetch")}</button></div></div>
          <div className="legacy-model-horizontal-field"><label>{t("显示名称", "Display name")}</label><input value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} placeholder={t("不填时默认跟随模型名称", "Defaults to the model name")} disabled={saving} /></div>
          <div className="legacy-model-horizontal-field"><label>{t("输入价格（美元 / 百万 Token）", "Input price (USD / million tokens)")}</label><input inputMode="decimal" value={form.inputCostPerMillionUSD} onChange={(event) => { setForm({ ...form, inputCostPerMillionUSD: event.target.value }); setFeedback(null); }} placeholder={t("例如 10；留空表示不改动价格，0 表示不收费", "e.g. 10; Leave blank to keep the current price; 0 means free")} disabled={saving} /></div>
          <div className="legacy-model-horizontal-field"><label>{t("输出价格（美元 / 百万 Token）", "Output price (USD / million tokens)")}</label><input inputMode="decimal" value={form.outputCostPerMillionUSD} onChange={(event) => { setForm({ ...form, outputCostPerMillionUSD: event.target.value }); setFeedback(null); }} placeholder={t("例如 50；留空表示不改动价格，0 表示不收费", "e.g. 50; Leave blank to keep the current price; 0 means free")} disabled={saving} /></div>
          <div className="legacy-model-horizontal-field"><label>{t("缓存读取价格（美元 / 百万 Token）", "Cached input price (USD / million tokens)")}</label><input inputMode="decimal" value={form.cacheReadCostPerMillionUSD} onChange={(event) => { setForm({ ...form, cacheReadCostPerMillionUSD: event.target.value }); setFeedback(null); }} placeholder={t("例如 1；留空表示不改动价格，0 表示不收费", "e.g. 1; Leave blank to keep the current price; 0 means free")} disabled={saving} /></div>
        </div>
        <ModelOptions model={form.selectedModel} disabled={saving} summaryAvailable={form.compatibility === "openai"} t={t} onChange={(selectedModel) => { setForm({ ...form, selectedModel }); setFeedback(null); }} />
        <div className="legacy-model-horizontal-field legacy-operation-reason-field"><label htmlFor="provider-operation-reason">{t("操作原因", "Operation reason")}<em>*</em></label><input id="provider-operation-reason" value={operationReason} onChange={(event) => { setOperationReason(event.target.value); setFeedback(null); }} placeholder={t("请说明本次配置原因，将写入操作审计", "Describe this configuration change; it will be recorded in the operation audit")} maxLength={512} autoComplete="off" required disabled={saving} /></div>
        {feedback ? <p className={`legacy-model-feedback ${feedback.tone}`}>{feedback.text}</p> : null}
      </div>
      <footer><button type="button" className="legacy-secondary-button" disabled={saving} onClick={onClose}>{t("取消", "Cancel")}</button><button type="button" className="legacy-primary-button" disabled={!canSubmit || saving} onClick={() => void save()}>{saving ? t("保存中…", "Saving…") : t("保存", "Save")}</button></footer>
    </section>
    {pickerModels ? <ModelPicker models={pickerModels} initialID={form.selectedModel.id} t={t} onClose={() => setPickerModels(null)} onConfirm={(selectedModel) => { const merged = mergeFetchedModel(form.selectedModel, selectedModel); setForm({ ...form, selectedModel: merged }); setRemoteModelCatalog((catalog) => catalogWithSelected(catalog, merged)); setPickerModels(null); }} /> : null}
  </div>;
}

export function mergeFetchedModel(current: LLMGatewayRemoteModel, incoming: LLMGatewayRemoteModel): LLMGatewayRemoteModel {
  const manual = (path: string) => ["manual", "openrouter_reference"].includes(current.capability_sources[path]);
  return {
    ...incoming,
    capabilities: {
      ...incoming.capabilities,
      context_length: manual("context_length") ? current.capabilities.context_length : incoming.capabilities.context_length,
      max_completion_tokens: manual("max_completion_tokens") ? current.capabilities.max_completion_tokens : incoming.capabilities.max_completion_tokens,
      reasoning: {
        ...incoming.capabilities.reasoning,
        supported: manual("reasoning.supported") ? current.capabilities.reasoning.supported : incoming.capabilities.reasoning.supported,
        supported_efforts: manual("reasoning.supported_efforts") ? current.capabilities.reasoning.supported_efforts : incoming.capabilities.reasoning.supported_efforts,
        default_effort: manual("reasoning.default_effort") ? current.capabilities.reasoning.default_effort : incoming.capabilities.reasoning.default_effort
      },
      // 重新发现模型时不能把管理员声明（或已采纳）的输入模态冲掉，否则"发现模型"
      // 会静默把图片能力收回，图片识别随之失效。
      architecture: {
        ...incoming.capabilities.architecture,
        input_modalities: manual(INPUT_MODALITIES_SOURCE_KEY) ? current.capabilities.architecture.input_modalities : incoming.capabilities.architecture.input_modalities
      }
    },
    options: {
      reasoning_effort: current.options.reasoning_effort ?? incoming.options.reasoning_effort,
      // Enabling summaries asserts support for this exact provider/model route.
      // Rediscovery preserves the choice, but selecting a different model resets it.
      reasoning_summary: current.id === incoming.id ? current.options.reasoning_summary ?? null : null
    },
    capability_sources: {
      ...incoming.capability_sources,
      ...Object.fromEntries(Object.entries(current.capability_sources).filter(([, source]) => ["manual", "openrouter_reference"].includes(source)))
    }
  };
}

function DeleteDialog({ provider, csrfToken, t, onClose, onDeleted }: {
  provider: LLMGatewayProvider;
  csrfToken: string;
  t: Translator;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const name = provider.display_name || provider.model_name;
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  async function remove() {
    if (confirmation !== name || deleting) return;
    setDeleting(true);
    setError("");
    try {
      await deleteLLMGatewayProvider(csrfToken, t("删除模型供应商", "Delete model provider"), provider.id);
      await onDeleted();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("删除失败", "Delete failed"));
      setDeleting(false);
    }
  }
  return <div className="legacy-modal-backdrop" onMouseDown={(event) => { if (!deleting && event.target === event.currentTarget) onClose(); }}><section className="legacy-delete-dialog" role="dialog" aria-modal="true"><header><h2>{t("删除模型供应商", "Delete model provider")}</h2></header><div className="legacy-delete-body"><p>{t("此操作无法撤销。请输入模型供应商名称", "This action cannot be undone. Enter the model provider name")} <strong>{name}</strong> {t("以确认删除。", "to confirm deletion.")}</p><input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={t("输入模型供应商名称", "Enter model provider name")} disabled={deleting} />{error ? <p className="legacy-model-feedback error">{error}</p> : null}</div><footer><button type="button" className="legacy-secondary-button" disabled={deleting} onClick={onClose}>{t("取消", "Cancel")}</button><button type="button" className="legacy-danger-button" disabled={confirmation !== name || deleting} onClick={() => void remove()}>{deleting ? t("删除中…", "Deleting…") : t("删除", "Delete")}</button></footer></section></div>;
}

const MODALITIES: Array<{ key: keyof LLMGatewayAttachmentParserModels; label: [string, string]; hint: [string, string] }> = [
  { key: "image", label: ["图片", "Image"], hint: ["png / jpeg / webp …", "png / jpeg / webp …"] },
  { key: "audio", label: ["音频", "Audio"], hint: ["wav / mp3 / m4a …(需 omni 模型)", "wav / mp3 / m4a … (omni model required)"] },
  { key: "video", label: ["视频", "Video"], hint: ["mp4 / mov …(需 omni / vl 模型)", "mp4 / mov … (omni / vl model required)"] }
];

function AttachmentParserPanel({ providers, csrfToken, t }: { providers: LLMGatewayProvider[]; csrfToken: string; t: Translator }) {
  const [selected, setSelected] = useState<LLMGatewayAttachmentParserModels>({ image: "", audio: "", video: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void getLLMGatewayAttachmentParsers().then((models) => { if (active) setSelected(models); }).catch((caught) => { if (active) setError(caught instanceof Error ? caught.message : String(caught)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  async function save() {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      setSelected(await updateLLMGatewayAttachmentParsers(csrfToken, t("更新多模态附件解析模型", "Update multimodal attachment parser models"), selected));
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setSaving(false);
    }
  }
  return <section className="legacy-attachment-panel">
    <h3>{t("多模态附件解析模型（按类型）", "Multimodal attachment parser models (by type)")}</h3>
    <p>{t("为每种附件类型选一个支持该模态的模型作为「翻译官」。用户上传文件时，系统按类型路由到对应模型把附件转写成文字，再交给主力对话模型（主力可以是非多模态）。某类型留空则不解析该类型；图片留空时仍可把原图直接发给多模态主力模型。", "Choose a model that supports each attachment type as a translator. Attachments are transcribed before being sent to the primary model. Leave a type empty to disable parsing; images may still be sent directly to a multimodal primary model.")}</p>
    {error ? <p className="legacy-model-feedback error">{error}</p> : null}
    <div className="legacy-attachment-fields">{MODALITIES.map((modality) => <label key={modality.key}><span><strong>{t(...modality.label)}</strong><small>{t(...modality.hint)}</small></span><select value={selected[modality.key]} onChange={(event) => { setSelected({ ...selected, [modality.key]: event.target.value }); setSaved(false); }} disabled={loading}><option value="">{t(`（不解析${modality.label[0]}）`, `Do not parse ${modality.label[1].toLowerCase()}`)}</option>{providers.map((provider) => <option key={provider.model_name} value={provider.model_name}>{provider.display_name || provider.model_name}</option>)}</select></label>)}</div>
    <div className="legacy-attachment-actions"><button type="button" className="legacy-primary-button" disabled={loading || saving} onClick={() => void save()}>{saving ? t("保存中…", "Saving…") : t("保存", "Save")}</button>{saved ? <span>{t("已保存", "Saved")}</span> : null}</div>
  </section>;
}

export function LLMGatewayProviders({ csrfToken, loading, providers, refresh, t }: LLMGatewayProvidersProps) {
  const [items, setItems] = useState(providers);
  const [query, setQuery] = useState("");
  const [dialog, setDialog] = useState<{ type: "add" } | { type: "edit"; provider: LLMGatewayProvider } | null>(null);
  const [deleting, setDeleting] = useState<LLMGatewayProvider | null>(null);
  const [error, setError] = useState("");
  useEffect(() => setItems(providers), [providers]);
  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return keyword ? items.filter((provider) => (provider.display_name || provider.model_name).toLowerCase().includes(keyword)) : items;
  }, [items, query]);

  async function mutate(provider: LLMGatewayProvider, patch: Partial<LLMGatewayProviderInput>, reason: string) {
    const previous = items;
    if (patch.enabled !== undefined) setItems((current) => current.map((item) => item.id === provider.id ? { ...item, enabled: patch.enabled as boolean } : item));
    if (patch.is_default) setItems((current) => current.map((item) => ({ ...item, is_default: item.id === provider.id })));
    setError("");
    try {
      await upsertLLMGatewayProvider(csrfToken, reason, providerInput(provider, patch));
      await refresh();
    } catch (caught) {
      setItems(previous);
      setError(caught instanceof Error ? caught.message : t("保存失败", "Save failed"));
    }
  }

  return <>
    <div className="legacy-provider-area">
      {error ? <p className="legacy-provider-error">{error}</p> : null}
      <section className="legacy-provider-panel">
        <div className="legacy-provider-toolbar"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("搜索已添加的模型供应商", "Search added model providers")} autoComplete="off" /><button type="button" className="legacy-provider-add" title={t("添加模型供应商", "Add model provider")} aria-label={t("添加模型供应商", "Add model provider")} onClick={() => setDialog({ type: "add" })}><svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg></button></div>
        {loading && items.length === 0 ? <div className="legacy-provider-empty">{t("正在加载模型供应商…", "Loading model providers…")}</div>
          : filtered.length === 0 ? <div className="legacy-provider-empty">{query.trim() ? t("没有匹配的模型供应商", "No matching model providers") : t("尚未添加模型供应商", "No model providers have been added")}</div>
            : <ul className="legacy-provider-list">{filtered.map((provider) => <ProviderRow key={provider.id} provider={provider} t={t} onDefault={() => void mutate(provider, { is_default: true }, t("设置默认模型供应商", "Set default model provider"))} onEdit={() => setDialog({ type: "edit", provider })} onDelete={() => setDeleting(provider)} onToggle={(enabled) => void mutate(provider, { enabled }, t(enabled ? "启用模型供应商" : "停用模型供应商", enabled ? "Enable model provider" : "Disable model provider"))} />)}</ul>}
      </section>
      <AttachmentParserPanel providers={items} csrfToken={csrfToken} t={t} />
    </div>
    {dialog ? <ProviderDialog key={dialog.type === "edit" ? dialog.provider.id : "add"} provider={dialog.type === "edit" ? dialog.provider : null} csrfToken={csrfToken} t={t} onClose={() => setDialog(null)} onSaved={refresh} /> : null}
    {deleting ? <DeleteDialog provider={deleting} csrfToken={csrfToken} t={t} onClose={() => setDeleting(null)} onDeleted={refresh} /> : null}
  </>;
}
